import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench } from "lucide-react";
import { ROUTES } from "@/config/constants";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { PageLoader } from "@/components/ui/Loader";
import { upperLocacaoText } from "@/pages/Locacoes/upperLocacaoText";
import {
  MANUTENCAO_PRIORIDADE_LABELS,
  MANUTENCAO_TIPO_LABELS,
  createManutencao,
  extractApiError,
  fetchLocacoesSnapshot,
  type LocacoesSnapshot,
  type ManutencaoPrioridade,
  type ManutencaoTipo,
} from "@/services/locacoes";
import { filterMaquinasPesadas } from "./operadorUtils";

const TIPOS: ManutencaoTipo[] = ["preventiva", "corretiva", "revisao", "troca_oleo"];
const PRIORIDADES: ManutencaoPrioridade[] = ["baixa", "media", "alta"];

export function OperadorManutencaoPage() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<LocacoesSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [maquinaId, setMaquinaId] = useState("");
  const [tipo, setTipo] = useState<ManutencaoTipo>("corretiva");
  const [prioridade, setPrioridade] = useState<ManutencaoPrioridade>("media");
  const [descricao, setDescricao] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSnapshot(await fetchLocacoesSnapshot());
    } catch (e) {
      setError(extractApiError(e, "Erro ao carregar máquinas."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const maquinarios = useMemo(
    () => filterMaquinasPesadas(snapshot?.maquinariosScoped ?? snapshot?.maquinarios ?? []),
    [snapshot],
  );

  const elegiveis = maquinarios.filter((m) => m.status !== "inativo");

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    if (!maquinaId) {
      setError("Selecione a máquina.");
      return;
    }
    if (!descricao.trim()) {
      setError("Descreva o problema ou serviço necessário.");
      return;
    }
    const selected = maquinarios.find((m) => m.id === maquinaId);
    setBusy(true);
    try {
      await createManutencao({
        maquinaId,
        tipo,
        descricao: descricao.trim(),
        dataAbertura: new Date().toISOString().slice(0, 10),
        horimetroAtual: selected?.horimetroAtual,
        prioridade,
      });
      setSuccess("Solicitação enviada. Aguarde aprovação da engenharia.");
      setMaquinaId("");
      setDescricao("");
      setTipo("corretiva");
      setPrioridade("media");
      setTimeout(() => navigate(ROUTES.OPERADOR), 1500);
    } catch (e) {
      setError(extractApiError(e, "Erro ao enviar solicitação."));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !snapshot) {
    return <PageLoader label="Carregando..." />;
  }

  if (elegiveis.length === 0) {
    return (
      <div className="mx-auto max-w-lg rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-center text-sm text-amber-900">
        {error ?? "Nenhuma máquina pesada disponível para solicitação de manutenção."}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
          <Wrench className="h-5 w-5 text-amber-600" />
        </div>
        <div>
          <h2 className="text-base font-bold text-slate-800">Solicitar manutenção</h2>
          <p className="text-xs text-slate-500">A ordem só será aberta após aprovação.</p>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-card">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>
        )}
        {success && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
            {success}
          </div>
        )}

        <Select
          label="Máquina *"
          value={maquinaId}
          onChange={(e) => setMaquinaId(e.target.value)}
          options={[
            { value: "", label: "Selecione…" },
            ...elegiveis.map((m) => ({ value: m.id, label: `${m.nome} (${m.tipo})` })),
          ]}
        />

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as ManutencaoTipo)}
            options={TIPOS.map((t) => ({ value: t, label: MANUTENCAO_TIPO_LABELS[t] }))}
          />
          <Select
            label="Prioridade"
            value={prioridade}
            onChange={(e) => setPrioridade(e.target.value as ManutencaoPrioridade)}
            options={PRIORIDADES.map((p) => ({ value: p, label: MANUTENCAO_PRIORIDADE_LABELS[p] }))}
          />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Descrição *</label>
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(upperLocacaoText(e.target.value))}
            rows={4}
            placeholder="Descreva o problema observado…"
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
          />
        </div>

        <Button type="button" className="w-full" loading={busy} onClick={() => void handleSubmit()}>
          Enviar solicitação
        </Button>
      </div>
    </div>
  );
}
